import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import {
  Box,
  IconButton,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  CircularProgress,
  alpha,
} from "@mui/material";
import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { JSX, useEffect, useState } from "react";
import { COLORS } from "../../assets/theme";
import { IField } from "../../types/table/interfaces.type";
import CustomCheckbox from "../CustomCheckbox";
import React from "react";
import { formatTableDate, formatTableDateTime } from "../../utils/tableDateFormat";

// A row click opens the row, unless it landed on a control inside it.
export const isFromControl = (e: React.MouseEvent) =>
  Boolean((e.target as HTMLElement).closest("button, a, input, select, textarea, label, [role='button'], [role='checkbox'], [role='menuitem']"));

interface IDataTableBodyProps<T> {
  fields: IField<T>[];
  content: T[];
  actions?: {
    label: string;
    icon: React.ReactNode | ((row: any) => React.ReactNode);
    handler: (row: T) => void;
  }[];
  renderActions?: (row: T) => React.ReactElement[];
  shadeRow?: (row: T) => boolean;
  shadeBg?: string | ((row: T) => string | undefined);
  shadeColor?: string;
  checkboxSelect?: boolean;
  onRowSelect?: (row: T, checked: boolean) => void;
  isRowSelected?: (row: T) => boolean;
  onRowClick?: (row: T) => void;
  highlights?: Record<string, boolean>[];
}

function DataTableBody<T>({
  fields,
  content = [],
  highlights = [],
  actions = [],
  renderActions,
  shadeRow,
  shadeBg,
  shadeColor,
  checkboxSelect = false,
  onRowSelect,
  isRowSelected,
  onRowClick,
}: IDataTableBodyProps<T>) {
  const theme = useTheme();

  const getRowSx = (row: T) => {
    if (shadeRow && shadeRow(row)) {
      let backgroundColor: string | undefined = "#FFEEAA";
      if (shadeBg) {
        backgroundColor =
          typeof shadeBg === "function" ? shadeBg(row) : shadeBg;
      }

      const textColor =
        shadeColor ??
        (theme.palette.mode === "light"
          ? theme.palette.grey[600]
          : theme.palette.grey[500]);

      return {
        backgroundColor,
        color: textColor,
        transition: theme.transitions.create(
          ["background-color", "background-image", "color"],
          { duration: theme.transitions.duration.shortest }
        ),
        borderRadius: "8px",
        "& td": {
          color: textColor,
          alignText: "center",
        },
      };
    }
    return {};
  };

  return (
    <TableBody
      sx={{
        "& .MuiTableCell-root": {
          padding: { xs: "8px 10px", sm: "6px 8px" },
          lineHeight: "18px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 180,
        },
        "& .MuiTableRow-root": {
          height: 44,
        },
        fontSize: { xs: "0.95rem", sm: "0.9rem" },
      }}
    >
      {content.length > 0 ? (
        content.map((row, rowIndex) => {
          const rowKey =
            (row as any)?.id ?? (row as any)?._id ?? (row as any)?.key ?? rowIndex;
          return (
          <TableRow
            key={String(rowKey)}
            onClick={onRowClick ? (e: React.MouseEvent) => { if (!isFromControl(e)) onRowClick(row); } : undefined}
            sx={{
              ...getRowSx(row),
              ...(onRowClick ? { cursor: "pointer" } : {}),
              ...(shadeRow && shadeRow(row)
                ? {}
                : {
                    backgroundColor:
                      rowIndex % 2 === 0
                        ? theme.palette.grey[100]
                        : theme.palette.common.white,
                  }),
              // Inset shadow tints over whatever background the row already
              // has, so shaded rows keep their colour on hover.
              "& > td": {
                transition: theme.transitions.create("box-shadow", {
                  duration: theme.transitions.duration.shortest,
                }),
              },
              "&:hover > td": {
                boxShadow: `inset 0 0 0 9999px ${alpha(
                  theme.palette.primary.main,
                  0.06
                )}`,
              },
            }}
          >
            {checkboxSelect && (
              <TableCell
                align="center"
                sx={{
                  borderBottom: "none",
                  textAlign: "center",
                  marginLeft: "center",
                }}
              >
                <Box sx={{ ml: 1 }}>
                  <CustomCheckbox
                    checked={isRowSelected ? isRowSelected(row) : false}
                    onChange={(e) =>
                      onRowSelect && onRowSelect(row, e.target.checked)
                    }
                    ariaLabel="Select row"
                  />
                </Box>
              </TableCell>
            )}
            {fields.map((field, fieldIndex) => (
              <AsyncTableCell<T>
                row={row}
                field={field}
                key={fieldIndex}
                highlight={highlights?.[rowIndex]?.[field.key] ?? false}
              />
            ))}
            {(() => {
              const actionElements = renderActions
                ? renderActions(row)
                : actions.map((action, actionIndex) => (
                    <IconButton
                      key={actionIndex}
                      size="small"
                      onClick={() => action.handler(row)}
                      aria-label={action.label}
                    >
                      {typeof action.icon === "function"
                        ? action.icon(row)
                        : action.icon}
                    </IconButton>
                  ));
              return actionElements.length > 0 ? (
                <TableCell sx={{ borderBottom: "none", textAlign: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {actionElements}
                  </Box>
                </TableCell>
              ) : null;
            })()}
          </TableRow>
        );
        })
      ) : (
        <TableRow>
          <TableCell
            colSpan={
              fields.length +
              (actions.length > 0 ? 1 : 0) +
              (checkboxSelect ? 1 : 0)
            }
            sx={{ textAlign: "center", borderBottom: "none", py: 5 }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.75,
                "@keyframes emptyStateIn": {
                  from: { opacity: 0, transform: "translateY(6px)" },
                  to: { opacity: 1, transform: "none" },
                },
                animation: "emptyStateIn 0.3s cubic-bezier(0.2, 0, 0, 1)",
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  mb: 0.5,
                }}
              >
                <InboxOutlinedIcon />
              </Box>
              <Typography sx={{ fontWeight: 600 }}>No data available</Typography>
              <Typography variant="body2" color="text.secondary">
                Nothing to show here yet.
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  );
}

interface AsyncTableCellProps<T> {
  key: number;
  row: T;
  field: IField<T>;
  highlight?: boolean;
}

function AsyncTableCell<T>({
  row,
  field,
  key,
  highlight = false,
}: AsyncTableCellProps<T>) {
  // Check if custom render function exists
  if (field.render) {
    return (
      <TableCell
        key={key}
        sx={{
          borderBottom: "none",
          textAlign: "center",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: highlight ? "#FFF59D" : undefined,
        }}
      >
        {field.render(row)}
      </TableCell>
    );
  }
  
  if (field.options) {
    return (
      <QueriedTableCell
        key={key}
        options={field.options(row)}
        highlight={highlight}
      />
    );
  }
  if (field.transform) {
    return (
      <PromisedTableCell
        promise={field.transform(row)}
        key={key}
        highlight={highlight}
      />
    );
  }
  const isStatus = field.key === "status";
  const isDescription = field.key === "description";
  const isDateField = field.key === "date";

  return (
    <TableCell
      key={key}
      sx={{
        borderBottom: "none",
        textAlign: isStatus ? "left" : "center",
        justifyContent: isStatus ? "flex-start" : "center",
        alignItems: "center",
        backgroundColor: highlight ? "#FFF59D" : undefined,
        width: isStatus ? "220px" : isDateField ? "fit-content" : "max-content",
        minWidth: isStatus ? 140 : isDateField ? 130 : undefined,
        maxWidth: isDescription || isDateField ? undefined : "150px",
        whiteSpace: isDateField ? "nowrap" : undefined,
        wordBreak: isDateField ? "keep-all" : "break-word",
        overflowWrap: isDateField ? "normal" : "break-word",
        pl: isStatus ? 2 : undefined,
      }}
    >
      {isStatus ? (
        <StatusBadge value={row[field.key]} />
      ) : (
        <SyncTableCellValue value={row[field.key]} />
      )}
    </TableCell>
  );
}

function StatusBadge({ value }: { value: any }) {
  const color =
    value === "Done"
      ? COLORS.SUCCESS
      : value === "Cancelled"
      ? COLORS.RED
      : COLORS.MainColor;

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            bgcolor: color,
            px: 1.25,
            py: 0.35,
            borderRadius: 2,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 60,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.9rem",
              textAlign: "center",
              color: COLORS.WhiteText,
            }}
          >
            {String(value ?? "-")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
interface PromisedTableCellProps {
  key: number;
  promise: Promise<any>;
  highlight?: boolean;
}

function PromisedTableCell({
  key,
  promise,
  highlight,
}: PromisedTableCellProps) {
  // show a spinner while promise is pending
  const [element, setElement] = useState<JSX.Element>(
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress size={18} thickness={4} sx={{ color: COLORS.MainColor }} />
    </Box>
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const newValue = await promise;
        if (!mounted) return;
        setElement(<SyncTableCellValue value={newValue} />);
      } catch (error: any) {
        if (!mounted) return;
        setElement(<ErrorComponent />);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [promise]);

  return (
    <TableCell
      key={key}
      sx={{
        borderBottom: "none",
        textAlign: "center",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: highlight ? "#FFF59D" : undefined,
        width: "max-content",
        maxWidth: "150px",
        overflowWrap: "break-word",
      }}
    >
      {element}
    </TableCell>
  );
}

function ErrorComponent() {
  return (
    <TableCell
      sx={{ borderBottom: "none", textAlign: "center", color: COLORS.RED }}
    >
      <WarningAmberIcon sx={{ color: COLORS.RED, width: 23, height: 23 }} />
    </TableCell>
  );
}

interface QueriedTableCellProps {
  options: UseQueryOptions<any, any>;
  key: number;
  highlight?: boolean;
}

function QueriedTableCell({ options, highlight }: QueriedTableCellProps) {
  const query = useQuery(options);

  const getDisplayValue = (data: unknown): JSX.Element => {
    let value = "";
    if (data == null) {
      value = "-";
    } else if (typeof data === "string" || typeof data === "number") {
      value = String(data);
    } else if (typeof data === "object") {
      const keys = Object.keys(data);
      if (keys.length === 1) {
        value = String((data as Record<string, any>)[keys[0]]);
      } else {
        value = JSON.stringify(data, null, 2);
      }
    } else {
      value = "-";
    }
    return <div style={{ textAlign: "center" }}>{value}</div>;
  };

  // show spinner while loading/fetching
  if (query.isLoading || query.isFetching) {
    return (
      <TableCell sx={{ borderBottom: "none", textAlign: "center" }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={18} thickness={4} sx={{ color: COLORS.MainColor }} />
        </Box>
      </TableCell>
    );
  }

  if (query.isError) {
    return (
      <TableCell sx={{ borderBottom: "none", textAlign: "center" }}>
        -
      </TableCell>
    );
  }

  return (
    <TableCell
      sx={{
        borderBottom: "none",
        textAlign: "center",
        justifyContent: "center",
        alignContent: "center",
        backgroundColor: highlight ? "#FFF59D" : undefined,
        width: "max-content",
        maxWidth: "150px",
        overflowWrap: "break-word",
      }}
    >
      <SyncTableCellValue value={getDisplayValue(query.data)} />
    </TableCell>
  );
}

interface SyncTableCellValueProps {
  value: any;
}

const iso8601DateTimeRegex =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/;
const isoDateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;

function isIsoDateTime(dateString: string): boolean {
  return iso8601DateTimeRegex.test(dateString);
}

function safeStringify(val: any): string {
  try {
    return JSON.stringify(val, null, 2);
  } catch (err) {
    return String(val);
  }
}

function SyncTableCellValue({ value }: SyncTableCellValueProps) {
  if (React.isValidElement(value)) {
    return value;
  }

  const formatValue = (val: any): string => {
    if (val === undefined || val === null) {
      return "-";
    }
    if (val instanceof Date) {
      return formatTableDateTime(val);
    }
    if (typeof val === "string" && isIsoDateTime(val)) {
      return formatTableDateTime(val);
    }
    if (typeof val === "string" && isoDateOnlyRegex.test(val)) {
      return formatTableDate(val);
    }
    if (typeof val === "object") {
      return safeStringify(val);
    }
    return String(val);
  };

  const formattedValue = formatValue(value);

  return value !== undefined && value !== null ? (
    typeof value === "object" ? (
      <Typography
        component="pre"
        sx={{
          margin: 0,
          fontSize: "0.90rem",
          textAlign: "center",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {formattedValue}
      </Typography>
    ) : (
      <Typography
        sx={{
          margin: 0,
          fontSize: "0.90rem",
          textAlign: "center",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {formattedValue}
      </Typography>
    )
  ) : (
    <Typography
      sx={{
        textAlign: "center",
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      -
    </Typography>
  );
}

// Shared with DataCardList so a row reads the same as a card on phones.
export { StatusBadge, SyncTableCellValue };

export default DataTableBody;
